/** @jsx jsx */
import React, { useState } from 'react';
import { css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import EditFilledIcon from '@atlaskit/icon/core/migration/edit--edit-filled';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, Inline, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useCrossJoinTeamEligible } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/controllers/use-cross-join-eligibility-check/main.tsx';
import { EngagementSpotlight } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/engagement-spotlight/main.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { cssFn as editItemIcon } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/utils/css-fn-utils/edit-icon-css-fn.tsx';
import { cssFn as hoverIconAfter } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/utils/css-fn-utils/hover-icon-after-css-fn.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { PROJECT_PAGES_SPOTLIGHT_KEY } from './constants.tsx';
import { ProjectPageMenuItemWithJoinTeamPulse } from './cross-join-team-nudge-provider/index.tsx';
import { EndUserActivateNudgeProvider } from './end-user-activate-nudge-provider/index.tsx';
import { messages } from './messages.tsx';
import type { PagesMenuItemProps } from './types.tsx';

const EditIcon = ({ onClick }: { onClick: PagesMenuItemProps['onEditClick'] }) => {
	const { formatMessage } = useIntl();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleClick = (ev: any) => {
		ev.preventDefault();
		if (onClick) {
			onClick(ev);
		}
	};
	// this function handle keydown event when user navigate to the edit button and press enter to open the dialog.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (event.key === 'Enter') {
			handleClick(event);
		}
	};

	const buttonProps: React.ComponentProps<typeof Button> = {
		appearance: 'subtle-link',
		spacing: 'none',
		onClick: handleClick,
		onKeyDown: handleKeyDown,
	};
	return (
		<Button
			data-edit-icon
			{...buttonProps}
			aria-describedby="navigation-apps-sidebar-common.ui.shortcuts.business-pages-link"
		>
			<EditFilledIcon
				color={token('color.icon')}
				LEGACY_size="medium"
				spacing="spacious"
				label={formatMessage(
					fg('blu-6205_misc_nav3_a11y_fixes') ? messages.editProjectPages : messages.edit,
				)}
			/>
		</Button>
	);
};

const WrappedPagesMenuItemWithMayBeLozenge = ({
	onEditClick,
	iconAfter,
	pagesMenuItemTitle,
	crossJoinNudge = {
		isCrossJoinTeamEligible: false,
		canShowJoinTeamlozenge: false,
		canShowJoinTeamPulse: false,
	},
	// Extracting here so that it does not end up in the "rest" which would eventually get passed to a normal DOM element and cause React to be unhappy
	projectId,
	...rest
}: PagesMenuItemProps) => {
	const { formatMessage } = useIntl();
	const { isCrossJoinTeamEligible, canShowJoinTeamlozenge } = crossJoinNudge;

	if (fg('blu-4174-enghealth-13134-page-item-a11y-fix')) {
		const icon =
			!iconAfter && onEditClick ? (
				<IconButton
					icon={EditFilledIcon}
					onClick={(e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) =>
						onEditClick(e)
					}
					label={formatMessage(
						fg('blu-6205_misc_nav3_a11y_fixes') ? messages.editProjectPages : messages.edit,
					)}
					appearance="subtle"
				/>
			) : (
				iconAfter
			);
		// In order to ensure that the spacing is correct in the Nav3 sidebar we need to wrap the icon in an element
		// with some padding. This ensures that everything lines up correctly when the menu item is selected.
		const { iconBefore, ...allTheRest } = rest;
		const augmentedIconBefore = <Box paddingInlineStart="space.200">{iconBefore}</Box>;
		return (
			<Inline alignBlock="center">
				<MenuItem iconBefore={augmentedIconBefore} {...allTheRest}>
					<Box paddingInlineStart="space.100">
						<Text id="navigation-apps-sidebar-common.ui.shortcuts.business-pages-link">
							{formatMessage(pagesMenuItemTitle || messages.projectPagesItem)}
						</Text>
						{isCrossJoinTeamEligible && canShowJoinTeamlozenge && (
							<span css={lozengeStyles} test-id="shortcuts.business-pages-lozenge">
								<Lozenge appearance="new">{formatMessage(messages.projectPagesLozenge)}</Lozenge>
							</span>
						)}
					</Box>
				</MenuItem>
				{icon}
			</Inline>
		);
	}

	const cssFn = isCrossJoinTeamEligible ? editItemIcon : hoverIconAfter;
	const extraProps =
		!iconAfter && onEditClick
			? { iconAfter: <EditIcon onClick={onEditClick} />, cssFn }
			: { iconAfter };

	return (
		<MenuItem {...rest} {...extraProps}>
			<Text id="shortcuts.business-pages-lozenge">
				{formatMessage(pagesMenuItemTitle || messages.projectPagesItem)}
			</Text>
			{isCrossJoinTeamEligible && canShowJoinTeamlozenge && (
				<span css={lozengeStyles} test-id="shortcuts.business-pages-lozenge">
					<Lozenge appearance="new">{formatMessage(messages.projectPagesLozenge)}</Lozenge>
				</span>
			)}
		</MenuItem>
	);
};

const lozengeStyles = css({
	paddingLeft: token('space.100'),
});

const WrappedPagesMenuItem = (props: PagesMenuItemProps) => (
	<>
		<SpotlightTarget name={PROJECT_PAGES_SPOTLIGHT_KEY}>
			{props.crossJoinNudge && props.crossJoinNudge.isCrossJoinTeamEligible ? (
				<ProjectPageMenuItemWithJoinTeamPulse showPulse={props.crossJoinNudge.canShowJoinTeamPulse}>
					<WrappedPagesMenuItemWithMayBeLozenge {...props} />
				</ProjectPageMenuItemWithJoinTeamPulse>
			) : (
				<WrappedPagesMenuItemWithMayBeLozenge {...props} />
			)}
		</SpotlightTarget>
		<EngagementSpotlight engagementId={PROJECT_PAGES_SPOTLIGHT_KEY} />
	</>
);

const WrappedPagesMenuItemWithNudge = (props: PagesMenuItemProps) => {
	// for a11y purposes, we drill the focussed state down so the nudge can be shown via keyboard controls
	const [isFocussed, setIsFocussed] = useState<boolean>(false);
	return (
		<ErrorBoundary
			id="pages-menu-item-end-user-activate-nudge"
			packageName="jira-navigation-apps-sidebar-common"
			render={() => <WrappedPagesMenuItem {...props} />}
		>
			<EndUserActivateNudgeProvider isFocussed={isFocussed}>
				<WrappedPagesMenuItem
					{...props}
					onFocus={(e) => {
						setIsFocussed(true);
						props.onFocus?.(e);
					}}
					onBlur={(e) => {
						setIsFocussed(false);
						props.onBlur?.(e);
					}}
				/>
			</EndUserActivateNudgeProvider>
		</ErrorBoundary>
	);
};

export const PagesMenuItem = (props: PagesMenuItemProps) => {
	const [{ route }] = useRouter();

	const crossJoinNudge = useCrossJoinTeamEligible(props.projectId);
	const extraProps = { ...props, ...{ crossJoinNudge } };
	// don't show the nudge if the user is already on project-pages
	return route.name !== 'project-pages' ? (
		<WrappedPagesMenuItemWithNudge {...extraProps} />
	) : (
		<WrappedPagesMenuItem {...extraProps} />
	);
};
